<template>
  <div>
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :endpoint="endpoint"
      :autoLoad="true"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @edit-click="handleEditClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import DataTable from '../../grid/DataTable.vue';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

const emit = defineEmits<{
  edit: [id: Id];
  delete: [id: Id];
}>();

const { t } = useI18n();

const datatableRef = ref<DataTableInstance | null>(null);

const endpoint = computed<string>(() => '/index.php/so/additional_services/lista');

const tableColumns = computed(() => {
  return [
    {
      data: 'so_additional_service_id',
      title: t('ID'),
      width: 80,
      sortable: true,
      filterType: 'text',
      renderType: 'clickable_id'
    },
    {
      data: 'so_additional_service_name',
      title: t('Nome'),
      width: 250,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'so_service_category_name',
      title: t('Categoria'),
      width: 200,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'so_additional_service_default_price',
      title: t('Preco'),
      width: 120,
      sortable: true,
      renderType: 'currency'
    },
    {
      data: 'so_additional_service_additional_deadline_days',
      title: t('Prazo Adicional'),
      width: 120,
      sortable: true
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    filename: `so_additional_services_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 25,
    pagination: true,
    rowSelection: 'single',
    sortable: true,
    resizableColumns: true,
    showFilters: true,
    filterDisplay: 'row',
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const refreshButtonConfig = computed(() => ({ show: true, text: t('Atualizar') }));
const csvButtonConfig = computed(() => ({ show: true, text: t('CSV') }));

const loadData = (): void => {
  log('SOAdditionalServicesTable', 'loadData', 'Triggering table refresh');
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('SOAdditionalServicesTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
};

const handleDataError = (error: { message?: string } | Error): void => {
  log('SOAdditionalServicesTable', 'handleDataError', 'Error loading data', error);
  const message = error instanceof Error ? error.message : (error.message || '');
  eventBus.emit('globalMessage.error', t('Erro ao carregar serviços adicionais') + ': ' + message);
};

const handleEditClick = (id: Id): void => {
  log('SOAdditionalServicesTable', 'handleEditClick', 'Edit clicked for ID', id);
  emit('edit', id);
};

const handleSavedOrDeleted = (): void => loadData();

onMounted(() => {
  log('SOAdditionalServicesTable', 'mounted', 'Setting up event listeners');
  eventBus.on('so-additional-services.saved', handleSavedOrDeleted);
  eventBus.on('so-additional-services.deleted', handleSavedOrDeleted);
});

onBeforeUnmount(() => {
  eventBus.off('so-additional-services.saved', handleSavedOrDeleted);
  eventBus.off('so-additional-services.deleted', handleSavedOrDeleted);
});

defineExpose({ loadData });
</script>
